import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { doc, collection, getDocs, deleteDoc, setDoc, serverTimestamp, getDoc, onSnapshot, query, where, documentId, writeBatch } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components/Spinner/Spinner';
import { RatingStrawberries } from '../../components/RatingStrawberries';
import { 
  getShowRatings, 
  addRating, 
  addTagToShow, 
  removeTagFromShow, 
  getShowTags, 
  markShowAsSeen, 
  ensureAuthenticated,
  getWatchlistShows,
  getSeenShows,
  getWatchingShows,
  addToWatchlist, 
  markShowAsWatching,
  archiveShow 
} from '../../services/databaseService';
import './Watchlist.css';
import LoginPrompt from '../../components/LoginPrompt/LoginPrompt';
import { RatingModal } from '../../components/RatingModal';
import { RatingOnlyModal } from '../../components/RatingOnlyModal';
import { getStreamingSources } from '../../services/watchmodeApi';
import RatingsOverviewModal from '../../components/RatingsOverviewModal/RatingsOverviewModal';
import { pageStateStore } from '../../services/pageStateStore';
import { useLocation } from 'react-router-dom';
import { getUserRating } from '../../services/databaseService';
import { TagModal } from '../../components/TagModal/TagModal';
import { Toast } from '../../components/Toast/Toast';
import { getShowDetails } from '../../services/watchmodeApi';
import { useInView } from 'react-intersection-observer';
import { limit, orderBy, startAfter } from 'firebase/firestore';
import { ShowCard } from '../../components/ShowCard';
import { createPortal } from 'react-dom';

const ITEMS_PER_PAGE = 30;

const fetchShowDetails = async (showIds) => {
  try {
    const showDetails = {};
    const showsRef = collection(db, 'shows');

    // Process showIds in batches of 30
    for (let i = 0; i < showIds.length; i += 30) {
      const batch = showIds.slice(i, i + 30);
      const q = query(showsRef, where(documentId(), 'in', batch));
      const snapshot = await getDocs(q);

      snapshot.docs.forEach(doc => {
        showDetails[doc.id] = doc.data();
      });
    }

    // Fetch any missing shows from API
    const missingIds = showIds.filter(id => !showDetails[id]);
    if (missingIds.length > 0) {
      const apiResults = await Promise.all(
        missingIds.map(id => getShowDetails(id))
      );

      apiResults.forEach((show, index) => {
        if (show) {
          showDetails[missingIds[index]] = show;
        }
      });
    }

    return showDetails;
  } catch (error) {
    console.error('Error fetching show details:', error);
    return {};
  }
};

const fetchTagsForShows = async (showIds, userId) => {
  try {
    const allTags = {};
    await Promise.all(
      showIds.map(async (showId) => {
        const tags = await getShowTags(userId, showId);
        allTags[showId] = tags;
      })
    );
    return allTags;
  } catch (error) {
    console.error('Error fetching tags:', error);
    return {};
  }
};

const fetchRatingsForShows = async (showIds) => {
  try {
    const allRatings = {};
    await Promise.all(
      showIds.map(async (showId) => {
        const ratings = await getShowRatings(showId);
        allRatings[showId] = ratings;
      })
    );
    return allRatings;
  } catch (error) {
    console.error('Error fetching ratings:', error);
    return {};
  }
};

const uniqueById = (shows) => {
  const seen = new Set();
  return shows.filter(show => {
    if (seen.has(show.id)) {
      return false;
    }
    seen.add(show.id);
    return true;
  });
};

const StatusMenu = ({ show, position, onClose, currentStatus, onStatusChange }) => {
  return createPortal(
    <>
      <div className="popular-status-menu-overlay" onClick={onClose} />
      <div 
        className="popular-status-menu" 
        style={{ 
          position: 'fixed',
          left: `${position.left}px`,
          top: `${position.top}px`
        }}
      >
        <button 
          className={`status-option ${currentStatus === 'watching' ? 'active' : ''}`}
          onClick={() => onStatusChange(show, 'watching')}
        >
          <i className="fas fa-satellite-dish"></i>
          <span>Tittar på</span>
        </button>
        <button 
          className={`status-option ${currentStatus === 'watchlist' ? 'active' : ''}`}
          onClick={() => onStatusChange(show, 'watchlist')}
        >
          <i className="fas fa-check"></i>
          <span>Ska se</span>
        </button>
        <button 
          className={`status-option ${currentStatus === 'seen' ? 'active' : ''}`}
          onClick={() => onStatusChange(show, 'seen')}
        >
          <i className="fas fa-check-double"></i>
          <span>Har sett</span>
        </button>
        <button 
          className={`status-option ${currentStatus === 'archived' ? 'active' : ''}`}
          onClick={() => onStatusChange(show, 'archived')}
        >
          <i className="fas fa-archive"></i>
          <span>Arkivera</span>
        </button>
      </div>
    </>,
    document.body
  );
};

function Watchlist() {
  const mountedRef = useRef(false);

  const { user } = useAuth();

  // Separate state declarations
  const [activeTab, setActiveTab] = useState('watchlist');
  const [watchlist, setWatchlist] = useState([]);
  const [seenList, setSeenList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedShow, setSelectedShow] = useState(null);
  const [showRatingOnlyModal, setShowRatingOnlyModal] = useState(false);
  const [selectedShowForRating, setSelectedShowForRating] = useState(null);
  const [showRatingsOverview, setShowRatingsOverview] = useState(false);
  const [selectedShowRatings, setSelectedShowRatings] = useState([]);
  const [selectedShowForOverview, setSelectedShowForOverview] = useState(null);
  const [shows, setShows] = useState([]);
  const [loadingItems, setLoadingItems] = useState(new Set());
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedShowForTags, setSelectedShowForTags] = useState(null);
  const [showTags, setShowTags] = useState({});
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showToRemove, setShowToRemove] = useState(null);
  const [toastState, setToastState] = useState({
    show: false,
    message: '',
    showSpinner: false
  });
  const [archivedList, setArchivedList] = useState([]);
  const [lastDocs, setLastDocs] = useState({
    watchlist: null,
    seen: null,
    watching: null,
    archived: null
  });
  const [hasMore, setHasMore] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(() => {
    return pageStateStore.getState('watchlist')?.scrollPosition || 0;
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [watchingList, setWatchingList] = useState([]);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [selectedShowForStatus, setSelectedShowForStatus] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  
  // Add ref for scroll restoration
  const scrollRestored = useRef(false);

  // Add ref for intersection observer
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });

  const location = useLocation();

  // Store show IDs in a ref to prevent infinite loops
  const watchlistIdsRef = useRef([]);
  const seenListIdsRef = useRef([]);

  // Add this function
  const fetchShowRatings = async (show) => {
    setRatingsLoading(true);
    try {
      const ratingsData = await getShowRatings(show.id);
      const ratingsWithUserDetails = await Promise.all(
        ratingsData.ratings.map(async (rating) => {
          const userDoc = await getDoc(doc(db, 'users', rating.userId));
          const userData = userDoc.data();

          // Get user name with fallback logic for Apple Sign In
          let userName = 'Anonymous';
          if (userData?.displayName) {
            userName = userData.displayName;
          } else if (userData?.firstName || userData?.lastName) {
            userName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          }

          return {
            ...rating,
            userName,
            userPhoto: userData?.photoURL
          };
        })
      );
      setSelectedShowRatings(ratingsWithUserDetails);
    } catch (error) {
      console.error('Error fetching ratings:', error);
      setSelectedShowRatings([]);
    } finally {
      setRatingsLoading(false);
    }
  };

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const initializeWatchlist = async () => {
      try {
        await ensureAuthenticated();

        if (!user) return;

        setLoading(true);

        // Set up listeners for both watchlist and seen collections with pagination
        const watchlistUnsubscribe = onSnapshot(
          query(
            collection(db, `users/${user.uid}/watchlist`),
            orderBy('addedAt', 'desc'),
            limit(ITEMS_PER_PAGE)
          ),
          async (snapshot) => {
            const watchlistIds = snapshot.docs.map(doc => doc.id);
            const showDetails = await fetchShowDetails(watchlistIds);

            const basicWatchlist = uniqueById(
              watchlistIds
                .map(id => ({
                  ...showDetails[id],
                  id,
                  addedAt: snapshot.docs.find(doc => doc.id === id)?.data()?.addedAt,
                }))
                .filter(show => show.title)
            );

            setWatchlist(basicWatchlist);
            setLastDocs(prev => ({
              ...prev,
              watchlist: snapshot.docs[snapshot.docs.length - 1]
            }));
            setHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
          }
        );

        // Similar modification for seen collection
        const seenUnsubscribe = onSnapshot(
          query(
            collection(db, `users/${user.uid}/seen`),
            orderBy('addedAt', 'desc'),
            limit(ITEMS_PER_PAGE)
          ),
          async (snapshot) => {
            const seenIds = snapshot.docs.map(doc => doc.id);
            const showDetails = await fetchShowDetails(seenIds);

            const basicSeenList = uniqueById(
              seenIds
                .map(id => ({
                  ...showDetails[id],
                  id,
                  addedAt: snapshot.docs.find(doc => doc.id === id)?.data()?.addedAt,
                }))
                .filter(show => show.title)
            );

            setSeenList(basicSeenList);
            setLastDocs(prev => ({
              ...prev,
              seen: snapshot.docs[snapshot.docs.length - 1]
            }));
            setHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
          }
        );

        setLoading(false);

        return () => {
          watchlistUnsubscribe();
          seenUnsubscribe();
        };

      } catch (error) {
        console.error('Error initializing lists:', error);
        setLoading(false);
      }
    };

    initializeWatchlist();
  }, [user]);

  // Effect to restore scroll position after initial content is loaded
  useEffect(() => {
    if (!loading && (watchlist.length > 0 || seenList.length > 0) && !scrollRestored.current) {
      const savedPosition = pageStateStore.getState('watchlist')?.scrollPosition || 0;
      
      // Load more content if needed before restoring scroll position
      const loadMoreIfNeeded = async () => {
        // If saved position is greater than current content height, we need to load more
        const contentHeight = document.documentElement.scrollHeight;
        if (savedPosition > contentHeight * 0.8 && hasMore && !isLoadingMore) {
          await loadMore();
        }
        
        // Use setTimeout to ensure this happens after rendering
        setTimeout(() => {
          window.scrollTo(0, savedPosition);
          scrollRestored.current = true;
        }, 100);
      };

      loadMoreIfNeeded();
    }
  }, [loading, watchlist.length, seenList.length, hasMore, isLoadingMore]);

  // Separate effect for scroll handling
  useEffect(() => {
    if (!user || loading) return;

    const handleScroll = () => {
      // Only track scroll position if it wasn't triggered by our restoration
      if (scrollRestored.current) {
        const position = window.scrollY;
        setScrollPosition(position);
        pageStateStore.setState('watchlist', { scrollPosition: position });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [user, loading]);

  // Add effect for infinite scroll
  useEffect(() => {
    if (inView && hasMore && !isLoadingMore && !loading) {
      loadMore();
    }
  }, [inView, hasMore, isLoadingMore, loading]);

  // Clean up scroll position when component unmounts
  useEffect(() => {
    return () => {
      if (user && !loading) {
        pageStateStore.setState('watchlist', { scrollPosition });
      }
    };
  }, [scrollPosition, user, loading]);

  // Add logging to tag-related functions
  const handleAddTag = async (tag) => {
    try {
      if (user && selectedShowForTags) {
        await addTagToShow(user.uid, selectedShowForTags.id, tag);
        const updatedTags = await getShowTags(user.uid, selectedShowForTags.id);
        setShowTags(prev => ({
          ...prev,
          [selectedShowForTags.id]: updatedTags
        }));
      }
    } catch (error) {
      console.error('Error in handleAddTag:', error);
    }
  };

  const handleRemoveShow = async (show) => {
    try {
      setLoadingItems(prev => new Set(prev).add(show.id));

      if (activeTab === 'watchlist') {
        await deleteDoc(doc(db, `users/${user.uid}/watchlist/${show.id}`));
      } else {
        await deleteDoc(doc(db, `users/${user.uid}/seen/${show.id}`));
      }

      setLoadingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(show.id);
        return newSet;
      });
    } catch (error) {
      console.error('Error removing show:', error);
    }
  };

  const handleMarkAsSeen = async (show) => {
    if (!user) return;

    try {
      setLoadingItems(prev => new Set([...prev, show.id]));
      setToastState({
        show: true,
        message: 'Markerar som sedd...',
        showSpinner: true
      });

      await markShowAsSeen(user.uid, show);

      // Update local state
      setWatchlist(prev => prev.filter(s => s.id !== show.id));
      setSeenList(prev => [...prev, { ...show, addedAt: serverTimestamp() }]);

      setToastState({
        show: true,
        message: 'Markerad som sedd!',
        showSpinner: false
      });

      setTimeout(() => {
        setToastState({ show: false, message: '', showSpinner: false });
      }, 2000);

    } catch (error) {
      console.error('Error marking as seen:', error);
      setToastState({
        show: true,
        message: 'Kunde inte markera som sedd',
        showSpinner: false
      });
    } finally {
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(show.id);
        return next;
      });
    }
  };

  const showToastWithStates = async (action) => {
    try {
      setToastState({
        show: true,
        message: '',
        showSpinner: true
      });

      await action();

      setToastState({
        show: true,
        message: 'Sparad',
        showSpinner: false
      });

      setTimeout(() => {
        setToastState({ show: false, message: '', showSpinner: false });
      }, 2000);

    } catch (error) {
      console.error('Error:', error);
      setToastState({ show: false, message: '', showSpinner: false });
    }
  };

  const handleRatingSubmit = async (rating) => {
    try {
      if (user && selectedShowForRating) {
        const { averageRating, ratingCount } = await addRating(
          user.uid,
          selectedShowForRating.id,
          rating
        );

        // Update both watchlist and seenlist
        const updateList = list => list.map(show =>
          show.id === selectedShowForRating.id
            ? {
              ...show,
              averageRating,
              ratingCount
            }
            : show
        );

        setWatchlist(updateList(watchlist));
        setSeenList(updateList(seenList));
      }
    } catch (error) {
      console.error('Error submitting rating:', error);
    } finally {
      setShowRatingOnlyModal(false);
      setSelectedShowForRating(null);
    }
  };

  const handleShare = async (e, show) => {
    e.preventDefault();
    e.stopPropagation();

    if (navigator.share) {
      try {
        await navigator.share({
          text: `Kolla in ${show.title} på Smultron!`,
          url: `${window.location.origin}/show/${show.id}`
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(`${window.location.origin}/show/${show.id}`);
      } catch (error) {
        console.error('Error copying to clipboard:', error);
      }
    }
  };

  const handleAddToWatchlist = async (show) => {
    if (!user) return;

    try {
      setToastState({
        show: true,
        message: 'Sparar serie...',
        showSpinner: true
      });

      // First check if show exists in /shows collection
      const showRef = doc(db, 'shows', show.id.toString());
      const showDoc = await getDoc(showRef);

      if (!showDoc.exists()) {
        // If show doesn't exist, fetch complete data and save to /shows
        const [apiShowData, imdbDetails] = await Promise.all([
          getShowDetails(show.id),
          getIMDbDetails(show.imdb_id)
        ]);

        // Get streaming sources if they don't exist
        let streamingSources = show.streamingSources;
        if (!streamingSources) {
          streamingSources = await getStreamingSources(show.id);
        }

        // Prepare complete show data
        const completeShowData = {
          id: show.id,
          title: apiShowData.title || show.title,
          poster: apiShowData.poster || show.poster,
          backdrop: apiShowData.backdrop,
          plot_overview: apiShowData.plot_overview,
          year: show.year,
          imdb_id: show.imdb_id,
          imdb_rating: imdbDetails?.imdbRating,
          total_seasons: imdbDetails?.totalSeasons ? parseInt(imdbDetails.totalSeasons) : null,
          streamingSources: Array.isArray(streamingSources) ? streamingSources :
            typeof streamingSources === 'string' ? [streamingSources] : [],
          updatedAt: serverTimestamp()
        };

        // Save complete data to /shows collection
        await setDoc(showRef, completeShowData);
      }

      // Add reference to user's watchlist
      const watchlistRef = doc(db, `users/${user.uid}/watchlist/${show.id}`);
      await setDoc(watchlistRef, {
        id: show.id,
        addedAt: serverTimestamp()
      });

      setToastState({
        show: true,
        message: 'Serie sparad!',
        showSpinner: false
      });

      setTimeout(() => {
        setToastState({ show: false, message: '', showSpinner: false });
      }, 2000);

    } catch (error) {
      console.error('Error adding to watchlist:', error);
      setToastState({
        show: true,
        message: 'Kunde inte spara serien',
        showSpinner: false
      });
    }
  };

  const handleArchiveShow = async (show) => {
    if (!user) return;

    try {
      setLoadingItems(prev => new Set([...prev, show.id]));
      setToastState({
        show: true,
        message: 'Arkiverar...',
        showSpinner: true
      });

      await archiveShow(user.uid, show);

      // Update local state
      setWatchlist(prev => prev.filter(s => s.id !== show.id));
      setSeenList(prev => prev.filter(s => s.id !== show.id));
      setArchivedList(prev => [...prev, show]);

      setToastState({
        show: true,
        message: 'Arkiverad!',
        showSpinner: false
      });

      setTimeout(() => {
        setToastState({ show: false, message: '', showSpinner: false });
      }, 2000);

    } catch (error) {
      console.error('Error archiving show:', error);
      setToastState({
        show: true,
        message: 'Kunde inte arkivera',
        showSpinner: false
      });
    } finally {
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(show.id);
        return next;
      });
    }
  };

  // Modify loadMore function
  const loadMore = async () => {
    if (!hasMore || isLoadingMore || !lastDocs[activeTab]) return;

    try {
      setIsLoadingMore(true);
      const collectionPath = `users/${user.uid}/${activeTab}`;
      
      const snapshot = await getDocs(
        query(
          collection(db, collectionPath),
          orderBy('addedAt', 'desc'),
          startAfter(lastDocs[activeTab]),
          limit(ITEMS_PER_PAGE)
        )
      );

      if (!snapshot.empty) {
        const ids = snapshot.docs.map(doc => doc.id);
        const showDetails = await fetchShowDetails(ids);

        const newShows = ids
          .map(id => ({
            ...showDetails[id],
            id,
            addedAt: snapshot.docs.find(doc => doc.id === id)?.data()?.addedAt,
          }))
          .filter(show => show.title);

        // Update the correct list based on activeTab
        switch (activeTab) {
          case 'watchlist':
            setWatchlist(prev => uniqueById([...prev, ...newShows]));
            break;
          case 'seen':
            setSeenList(prev => uniqueById([...prev, ...newShows]));
            break;
          case 'watching':
            setWatchingList(prev => uniqueById([...prev, ...newShows]));
            break;
          case 'archived':
            setArchivedList(prev => uniqueById([...prev, ...newShows]));
            break;
        }

        // Update lastDoc for the current tab
        setLastDocs(prev => ({
          ...prev,
          [activeTab]: snapshot.docs[snapshot.docs.length - 1]
        }));
        
        setHasMore(snapshot.docs.length === ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more items:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const fetchShows = async () => {
      if (!user) return;
      setLoading(true);
      try {
        const [watchlistData, seenData, watchingData] = await Promise.all([
          getWatchlistShows(user.uid),
          getSeenShows(user.uid),
          getWatchingShows(user.uid)
        ]);

        setWatchlist(watchlistData);
        setSeenList(seenData);
        setWatchingList(watchingData);
      } catch (error) {
        console.error('Error fetching shows:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchShows();
  }, [user]);

  const handleProtectedAction = () => {
    if (!user) {
      setShowLoginPrompt(true);
      return false;
    }
    return true;
  };

  const handleStatusClick = (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    if (!handleProtectedAction()) return;
    
    // Get button position
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    // Position menu to the left of the button by default
    let left = buttonRect.left - 220; // menu width (200px) + gap (20px)
    let top = buttonRect.top;
    
    // If not enough space on the left, position to the right
    if (left < 16) {
      left = buttonRect.right + 20;
    }
    
    // If not enough space on either side, position below
    if (left + 200 > window.innerWidth - 16) {
      left = buttonRect.left;
      top = buttonRect.bottom + 8;
    }
    
    setMenuPosition({ left, top }); // Add this state variable
    setSelectedShowForStatus(show);
    setShowStatusMenu(true);
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'watching':
        return 'Tillagd i Tittar på!';
      case 'watchlist':
        return 'Tillagd i Ska se!';
      case 'seen':
        return 'Markerad som sedd!';
      case 'archived':
        return 'Arkiverad!';
      default:
        return 'Status uppdaterad!';
    }
  };

  const handleStatusChange = async (show, newStatus) => {
    if (!handleProtectedAction()) return;

    try {
      switch (newStatus) {
        case 'watchlist':
          await addToWatchlist(user.uid, show);
          setWatchlist(prev => [...prev, show]);
          setWatchingList(prev => prev.filter(s => s.id !== show.id));
          setSeenList(prev => prev.filter(s => s.id !== show.id));
          setArchivedList(prev => prev.filter(s => s.id !== show.id));
          break;
        case 'watching':
          await markShowAsWatching(user.uid, show);
          setWatchingList(prev => [...prev, show]);
          setWatchlist(prev => prev.filter(s => s.id !== show.id));
          setSeenList(prev => prev.filter(s => s.id !== show.id));
          setArchivedList(prev => prev.filter(s => s.id !== show.id));
          break;
        case 'seen':
          await markShowAsSeen(user.uid, show);
          setSeenList(prev => [...prev, show]);
          setWatchlist(prev => prev.filter(s => s.id !== show.id));
          setWatchingList(prev => prev.filter(s => s.id !== show.id));
          setArchivedList(prev => prev.filter(s => s.id !== show.id));
          break;
        case 'archived':
          await archiveShow(user.uid, show);
          setArchivedList(prev => [...prev, show]);
          setWatchlist(prev => prev.filter(s => s.id !== show.id));
          setWatchingList(prev => prev.filter(s => s.id !== show.id));
          setSeenList(prev => prev.filter(s => s.id !== show.id));
          break;
        default:
          break;
      }

      setShowStatusMenu(false);
      setSelectedShowForStatus(null);
      setToastState({
        show: true,
        message: getStatusMessage(newStatus),
        showSpinner: false
      });
    } catch (error) {
      console.error('Error changing status:', error);
      setToastState({
        show: true,
        message: 'Kunde inte ändra status',
        showSpinner: false
      });
    }
  };

  // Add this effect to handle tab changes
  useEffect(() => {
    // Reset hasMore when switching tabs
    setHasMore(true);
    setIsLoadingMore(false);
  }, [activeTab]);

  // ... rest of your component code ...

  return (
    <div className="watchlist-container">
      {!user ? (
        <LoginPrompt />
      ) : loading ? (
        <Spinner />
      ) : (
        <div className="watchlist-content">
          <div className="tab-container" style={{ 
            overflowX: 'auto', 
            whiteSpace: 'nowrap', 
            WebkitOverflowScrolling: 'touch', 
            msOverflowStyle: 'none', 
            scrollbarWidth: 'none' 
          }}>
            <button
              className={`tab-button ${activeTab === 'watching' ? 'active' : ''}`}
              onClick={() => setActiveTab('watching')}
              style={{ display: 'inline-block' }}
            >
              Tittar på
            </button>
            <button
              className={`tab-button ${activeTab === 'watchlist' ? 'active' : ''}`}
              onClick={() => setActiveTab('watchlist')}
              style={{ display: 'inline-block' }}
            >
              Ska se
            </button>
            <button
              className={`tab-button ${activeTab === 'seen' ? 'active' : ''}`}
              onClick={() => setActiveTab('seen')}
              style={{ display: 'inline-block' }}
            >
              Har sett
            </button>
          </div>

          <div className="shows-container" style={{ marginTop: '12px' }}>
            {(activeTab === 'watchlist'
              ? watchlist
              : activeTab === 'watching'
              ? watchingList
              : activeTab === 'seen'
              ? seenList
              : archivedList
            ).map((show) => (
              <ShowCard
                key={show.id}
                show={{
                  ...show,
                  poster: (!show.poster || show.poster === 'N/A') ? 'images/default-poster.jpg' : show.poster,
                  type: show.type?.toLowerCase() === 'film' || 
                        show.type?.toLowerCase() === 'movie' ? 'movie' : show.type
                }}
                showStatus={activeTab}
                hideIndex={true}
                onStatusClick={(e) => {
                  e.preventDefault();
                  handleStatusClick(e, show);
                }}
                onRatingClick={(e) => {
                  e.preventDefault();
                  setSelectedShowForRating(show);
                  setShowRatingOnlyModal(true);
                }}
                onShareClick={(e) => {
                  e.preventDefault();
                  handleShare(e, show);
                }}
                onRatingOverviewClick={(e) => {
                  e.preventDefault();
                  setSelectedShowForOverview(show);
                  setShowRatingsOverview(true);
                  fetchShowRatings(show);
                }}
                onStreamingSourceClick={(e) => {
                  e.preventDefault();
                  setSelectedShowForSources(show);
                  setShowStreamingModal(true);
                }}
              />
            ))}

            {/* Add loading trigger */}
            {hasMore && (
              <div ref={loadMoreRef} className="loading-trigger">
                {isLoadingMore && <Spinner />}
              </div>
            )}
          </div>
        </div>
      )}

      {showTagModal && selectedShowForTags && (
        <TagModal
          show={selectedShowForTags}
          onClose={() => {
            setShowTagModal(false);
            setSelectedShowForTags(null);
          }}
          onAddTag={async (tag) => {
            if (user) {
              await addTagToShow(user.uid, selectedShowForTags.id, tag);
              const updatedTags = await getShowTags(user.uid, selectedShowForTags.id);
              setShowTags(prev => ({
                ...prev,
                [selectedShowForTags.id]: updatedTags
              }));
            }
          }}
          onDeleteTag={async (tag) => {
            if (user) {
              await removeTagFromShow(user.uid, selectedShowForTags.id, tag);
              const updatedTags = await getShowTags(user.uid, selectedShowForTags.id);
              setShowTags(prev => ({
                ...prev,
                [selectedShowForTags.id]: updatedTags
              }));
            }
          }}
          existingTags={showTags[selectedShowForTags.id] || []}
        />
      )}

      {showRatingsOverview && selectedShowForOverview && (
        <RatingsOverviewModal
          show={selectedShowForOverview}
          ratings={selectedShowRatings}
          isLoading={ratingsLoading}
          onClose={() => {
            setShowRatingsOverview(false);
            setSelectedShowForOverview(null);
            setSelectedShowRatings([]);
          }}
        />
      )}

      {/* Other modals */}
      {showRatingModal && selectedShow && (
        <RatingModal
          show={selectedShow}
          onClose={() => {
            setShowRatingModal(false);
            setSelectedShow(null);
          }}
          onSubmit={handleRatingSubmit}
        />
      )}

      {showRatingOnlyModal && selectedShowForRating && (
        <RatingOnlyModal
          show={selectedShowForRating}
          onClose={() => {
            setShowRatingOnlyModal(false);
            setSelectedShowForRating(null);
          }}
          onSubmit={handleRatingSubmit}
        />
      )}

      {showConfirmModal && showToRemove && (
        <div className="modal-overlay" onClick={() => {
          setShowConfirmModal(false);
          setShowToRemove(null);
        }}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>Ta bort {showToRemove.title}?</h3>
            <div className="confirm-buttons">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowConfirmModal(false);
                  setShowToRemove(null);
                }}
              >
                Avbryt
              </button>
              <button
                className="confirm-button"
                onClick={async () => {
                  await handleRemoveShow(showToRemove);
                  setShowConfirmModal(false);
                  setShowToRemove(null);
                }}
              >
                Ta bort
              </button>
            </div>
          </div>
        </div>
      )}

      {toastState.show && (
        <Toast
          message={toastState.message}
          showSpinner={toastState.showSpinner}
          onClose={() => setToastState({ show: false, message: '', showSpinner: false })}
        />
      )}

      {showStatusMenu && selectedShowForStatus && (
        <StatusMenu
          show={selectedShowForStatus}
          position={menuPosition}
          onClose={() => {
            setShowStatusMenu(false);
            setSelectedShowForStatus(null);
          }}
          currentStatus={activeTab}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}

export default Watchlist; 